import { IconFilter, IconPlus, IconSortAscending, IconTrash } from "@tabler/icons-react";
import { Button, Card, IconButton, Input, NumberInput, Select, Switch, Text } from "@/components/ui";
import type { Catalogo, RepDefinicion, RepFiltro } from "../services/reporteria.api";
import * as ops from "../state/builder-ops";

const OPERADORES = [
  { value: "=", label: "igual a" },
  { value: "<>", label: "distinto de" },
  { value: ">", label: "mayor que" },
  { value: ">=", label: "mayor o igual" },
  { value: "<", label: "menor que" },
  { value: "<=", label: "menor o igual" },
  { value: "contains", label: "contiene" },
  { value: "startswith", label: "empieza con" },
  { value: "notcontains", label: "no contiene" },
  { value: "notstartswith", label: "no empieza con" },
  { value: "between", label: "entre" },
  { value: "isnull", label: "es vacío" },
  { value: "isnotnull", label: "no es vacío" },
];

const SIN_VALOR = new Set(["isnull", "isnotnull"]);

export function AjustesReporte({
  def,
  setDef,
  catalogo,
  avanzado = true,
}: {
  def: RepDefinicion;
  setDef: (f: (d: RepDefinicion) => RepDefinicion) => void;
  catalogo?: Catalogo;
  /** Modo avanzado · en simple se oculta el "Modo resumen (agrupar)". */
  avanzado?: boolean;
}) {
  // token de columna → etiqueta legible (los tokens son opacos · nunca deben
  // mostrarse como texto). Se arma del catálogo, que sí conoce la etiqueta.
  const etiquetaPorToken = new Map<string, string>();
  for (const t of catalogo?.tablas ?? [])
    for (const c of t.columnas) etiquetaPorToken.set(c.token, c.etiqueta);

  const columnas = def.columnas.map((c) => ({
    value: `${c.tablaAlias}|${c.token}`,
    label: c.etiqueta || etiquetaPorToken.get(c.token) || "Columna",
  }));

  function colDe(valor: string): { tablaAlias: string; token: string } {
    const [tablaAlias, token] = valor.split("|");
    return { tablaAlias, token };
  }

  return (
    <Card className="space-y-4 p-3">
      {/* Opciones */}
      <div className="flex flex-wrap items-center gap-4">
        {avanzado && (
          <Switch
            checked={!!def.agrupar}
            onCheckedChange={(v) => setDef((d) => ops.setAgrupar(d, v))}
            label="Modo resumen (agrupar)"
          />
        )}
        <div className="flex items-center gap-2">
          <Text size="sm" c="dimmed">
            Límite de filas
          </Text>
          <div className="w-28">
            <NumberInput
              min={1}
              value={def.limite ?? undefined}
              placeholder="sin límite"
              onChange={(v) => setDef((d) => ops.setLimite(d, typeof v === "number" ? v : null))}
            />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div>
        <div className="mb-1.5 flex items-center gap-1.5">
          <IconFilter size={15} className="text-muted-foreground" />
          <Text fw={600} size="sm">
            Filtros
          </Text>
        </div>
        <div className="space-y-1.5">
          {(def.filtros ?? []).map((f, i) => (
            <div key={i} className="flex flex-wrap items-center gap-1.5">
              <div className="w-48">
                <Select
                  size="sm"
                  placeholder="Columna"
                  value={f.tablaAlias && f.token ? `${f.tablaAlias}|${f.token}` : ""}
                  onValueChange={(v) => setDef((d) => ops.updateFiltro(d, i, colDe(v)))}
                  options={columnas}
                />
              </div>
              <div className="w-40">
                <Select
                  size="sm"
                  value={f.operador}
                  onValueChange={(v) => setDef((d) => ops.updateFiltro(d, i, { operador: v }))}
                  options={OPERADORES}
                />
              </div>
              {!SIN_VALOR.has(f.operador) && (
                <Input
                  className="w-36"
                  placeholder="valor"
                  value={f.valor ?? ""}
                  onChange={(e) => { const valor = e.currentTarget.value; setDef((d) => ops.updateFiltro(d, i, { valor })); }}
                />
              )}
              {f.operador === "between" && (
                <Input
                  className="w-36"
                  placeholder="y"
                  value={f.valor2 ?? ""}
                  onChange={(e) => { const valor2 = e.currentTarget.value; setDef((d) => ops.updateFiltro(d, i, { valor2 })); }}
                />
              )}
              <IconButton aria-label="Quitar filtro" variant="subtle" size="sm" onClick={() => setDef((d) => ops.removeFiltro(d, i))}>
                <IconTrash size={16} />
              </IconButton>
            </div>
          ))}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            disabled={columnas.length === 0}
            onClick={() =>
              setDef((d) => {
                const primera = d.columnas[0];
                const filtro: RepFiltro = {
                  tablaAlias: primera?.tablaAlias ?? "",
                  token: primera?.token ?? "",
                  operador: "=",
                  valor: "",
                };
                return ops.addFiltro(d, filtro);
              })
            }
          >
            Agregar filtro
          </Button>
        </div>
      </div>

      {/* Orden */}
      <div>
        <div className="mb-1.5 flex items-center gap-1.5">
          <IconSortAscending size={15} className="text-muted-foreground" />
          <Text fw={600} size="sm">
            Ordenar
          </Text>
        </div>
        <div className="space-y-1.5">
          {(def.orden ?? []).map((o, i) => (
            <div key={i} className="flex flex-wrap items-center gap-1.5">
              <div className="w-48">
                <Select
                  size="sm"
                  placeholder="Columna"
                  value={o.tablaAlias && o.token ? `${o.tablaAlias}|${o.token}` : ""}
                  onValueChange={(v) => setDef((d) => ops.updateOrden(d, i, colDe(v)))}
                  options={columnas}
                />
              </div>
              <div className="w-36">
                <Select
                  size="sm"
                  value={o.direccion}
                  onValueChange={(v) => setDef((d) => ops.updateOrden(d, i, { direccion: v }))}
                  options={[
                    { value: "asc", label: "Ascendente" },
                    { value: "desc", label: "Descendente" },
                  ]}
                />
              </div>
              <IconButton aria-label="Quitar orden" variant="subtle" size="sm" onClick={() => setDef((d) => ops.removeOrden(d, i))}>
                <IconTrash size={16} />
              </IconButton>
            </div>
          ))}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            disabled={columnas.length === 0}
            onClick={() =>
              setDef((d) => {
                const primera = d.columnas[0];
                return ops.addOrden(d, {
                  tablaAlias: primera?.tablaAlias ?? "",
                  token: primera?.token ?? "",
                  direccion: "asc",
                });
              })
            }
          >
            Agregar orden
          </Button>
        </div>
      </div>
    </Card>
  );
}
